/**
 * What a character can be made of: skin tones, hair styles, outfits, and the ready-made heads the
 * character builder offers. Data only — the drawings are in Figures.tsx.
 */

import type { Avatar } from '../lib/types'

/** Palette keys stored in the avatar, and the colours they draw. */
export const SKIN_TONES: Record<string, string> = {
  light: '#FAD9BE',
  fair: '#F1C7A6',
  medium: '#E0A87C',
  tan: '#C68642',
  brown: '#8D5524',
  deep: '#5C3A21',
}

/** Every style `DeveloperAvatar.HairStyles` lists. A missing one silently becomes the default. */
export type HairStyle = 'short' | 'buzz' | 'curly' | 'long' | 'ponytail' | 'bun' | 'bald' | 'afro' | 'spiky' | 'wavy' | 'bob'

export const HAIR_STYLES: HairStyle[] = ['short', 'buzz', 'curly', 'long', 'ponytail', 'bun', 'bald', 'afro', 'spiky', 'wavy', 'bob']

/** What is worn on top. A few ready bodies rather than parts, so every choice looks finished. */
export type Outfit = 'hoodie' | 'jacket' | 'tee' | 'sweater' | 'shirt' | 'armor' | 'robe' | 'jersey'
export const OUTFITS: Outfit[] = ['hoodie', 'jacket', 'tee', 'sweater', 'shirt', 'armor', 'robe', 'jersey']
/** The outfits a figure gets when it never chose one: today's, never a Pro one. */
export const EVERYDAY_OUTFITS: Outfit[] = ['hoodie', 'jacket', 'tee', 'sweater', 'shirt']

/** On the head (docs/refactor/47). "none" lets the season's hat show. */
export type HatKind = 'none' | 'cap' | 'propeller' | 'wizard' | 'helmet' | 'crown' | 'pirate'
export const HATS: HatKind[] = ['none', 'cap', 'propeller', 'wizard', 'helmet', 'crown', 'pirate']

/** On the face: the kids' accessories (docs/refactor/48), free for everyone. */
export type ExtraKind = 'none' | 'bubblegum' | 'stars'
export const EXTRAS: ExtraKind[] = ['none', 'bubblegum', 'stars']

/** Held in the hunting pose, in the racket's place. */
export type HeldKind = 'racket' | 'sword' | 'staff' | 'axe'
export const HELD_ITEMS: HeldKind[] = ['racket', 'sword', 'staff', 'axe']

/** A small companion floating beside them. */
export type CompanionKind = 'none' | 'dragon' | 'owl'
export const COMPANIONS: CompanionKind[] = ['none', 'dragon', 'owl']

/** The Pro items: the server's `DeveloperAvatar.Premium`, the same list. Everything else is free. */
export const PREMIUM_ITEMS: ReadonlySet<string> = new Set(['wizard', 'helmet', 'crown', 'pirate', 'sword', 'staff', 'axe', 'armor', 'robe', 'jersey', 'dragon', 'owl'])

export type FacialHair = 'none' | 'stubble' | 'moustache' | 'beard' | 'goatee'

/** Heads: hair and beard drawn to go together. */
export const HEADS: Array<{ hair: HairStyle; facialHair: FacialHair }> = [
  { hair: 'short', facialHair: 'none' },
  { hair: 'short', facialHair: 'stubble' },
  { hair: 'spiky', facialHair: 'none' },
  { hair: 'spiky', facialHair: 'beard' },
  { hair: 'wavy', facialHair: 'none' },
  { hair: 'wavy', facialHair: 'stubble' },
  { hair: 'buzz', facialHair: 'goatee' },
  { hair: 'curly', facialHair: 'none' },
  { hair: 'afro', facialHair: 'none' },
  { hair: 'afro', facialHair: 'beard' },
  { hair: 'bald', facialHair: 'beard' },
  { hair: 'bald', facialHair: 'moustache' },
  { hair: 'long', facialHair: 'none' },
  { hair: 'ponytail', facialHair: 'none' },
  { hair: 'bun', facialHair: 'none' },
  { hair: 'bob', facialHair: 'none' },
]

export const HAIR_COLORS = ['#1B1B1B', '#2E2A27', '#4A342A', '#7B4B28', '#B5562B', '#C9A227', '#E8D08A', '#9A9A9A', '#E9E9E9', '#3B6FD8', '#D95C9A', '#7E57C2']
export const SHIRT_COLORS = ['#1F2A44', '#3B5BDB', '#1F7A8C', '#2F9E68', '#E0A800', '#C2560B', '#B5462C', '#D6336C', '#6A4FB8', '#495057', '#F1F3F5', '#212529']

/** Headphones, by model. "none" is a choice too, so it is in the list the builder shows. */
export type HeadphoneModel = 'none' | 'headset' | 'studio' | 'gamer' | 'earbuds' | 'retro' | 'cat' | 'neckband'
export const HEADPHONES: HeadphoneModel[] = ['none', 'headset', 'studio', 'gamer', 'earbuds', 'retro', 'cat', 'neckband']

/** The notebook a character carries while their agent works, and keeps on the desk. */
export type LaptopModel = 'tablet' | 'graphite' | 'silver' | 'gamer' | 'classic' | 'white' | 'stickers'
export const LAPTOPS: LaptopModel[] = ['tablet', 'graphite', 'silver', 'gamer', 'classic', 'white', 'stickers']

export const DEFAULT_AVATAR: Avatar = {
  skinTone: 'medium',
  hair: 'short',
  hairColor: '#2E2A27',
  shirtColor: '#1F2A44',
  glasses: false,
  facialHair: 'none',
  gender: 'unspecified',
  outfit: 'hoodie',
  headset: false,
  headphones: 'none',
  laptop: 'tablet',
  hat: 'none',
  held: 'racket',
  companion: 'none',
  extra: 'none',
}

/** The slots that have Pro items (docs/refactor/47). */
const PRO_SLOTS = ['hat', 'held', 'outfit', 'companion'] as const

/**
 * The Pro items being tried on: a change into a Pro item, on a plan without them. What was already worn stays, as on
 * the server (LooksGate): only the change is asked for.
 */
export function tryingPro(avatar: Avatar, initial: Avatar | null, allowed: boolean): Array<(typeof PRO_SLOTS)[number]> {
  if (allowed) return []
  return PRO_SLOTS.filter((slot) => {
    const value = avatar[slot]
    return typeof value === 'string' && PREMIUM_ITEMS.has(value) && value !== initial?.[slot]
  })
}
